import { identity } from 'ramda';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table';
import { ADMINISTRATOR_USER, INSTITUTION_USER } from '../../../actionTypes/Users/UserActionType';

const sortChevronClassNameMap = {
  descending: 'fa-caret-down',
  ascending: 'fa-caret-up',
};

const headerObj = [
  {
    name: 'toggleIcon',
    label: '',
  },
  {
    name: 'familyName',
    label: 'Last Name',
  },
  {
    name: 'givenName',
    label: 'First Name',
  },
  {
    name: 'email',
    label: 'Email',
  },
  {
    name: 'institutionName',
    label: 'Institution',
  },
  {
    name: 'casNames',
    label: 'CAS',
  },
  {
    name: 'clientNames',
    label: 'Organization',
  },
  {
    name: 'userRole',
    label: 'Role',
  },
  {
    name: 'status',
    label: 'Status',
  },
];

function UsersTable(props) {
  const { tData, loggedAs, onUserClick, refreshUsers, searchWords, id = null, } = props;
  const [sortByName, setSortByName] = useState('');
  const [sortByOrder, setSortByOrder] = useState('');
  const [unfoldedRowsID, setUnfoldedRowsID] = useState([]);
  const [unfoldToggleActive, setUnfoldToggle] = useState(false);
  const [headerColumn, setHeaderColumn] = useState([]);
  const [bodyColumn, setBodyColumn] = useState([]);
  const [dimensions, setDimensions] = useState({ 
    height: window.innerHeight,
    width: window.innerWidth
  });
  const sideNavToggle = useSelector(state => state.Page.sideNavToggle);

  const canViewColumnByRole = (headerObj) => {
    if (loggedAs === ADMINISTRATOR_USER) {
      return headerObj.filter((el) => el.name !== 'clientNames');
    }
    if (loggedAs === INSTITUTION_USER) {
      return headerObj.filter((el) => el.name !== 'institutionName');
    }
    return headerObj;
  };

  const dynamicSort = (property) => {
    const sortOrder = sortByOrder === 'descending' ? -1 : 1;
    return (a, b) => {
      const result = a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
      return result * sortOrder;
    };
  };

  const onClickUnfoldRow = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    const rowIDs = [...unfoldedRowsID];

    if (rowIDs.includes(index)) {
      rowIDs.splice(rowIDs.indexOf(index), 1);
    } else {
      rowIDs.push(index);
    }
    setUnfoldedRowsID(rowIDs);
  };

  const sort = (name) => {
    let orderToSort = '';
    if (sortByName !== name || sortByOrder === '') {
      orderToSort = 'ascending';
    } else if (sortByOrder === 'ascending') {
      orderToSort = 'descending';
    } else {
      refreshUsers();
      name = '';
    }
    setSortByName(name);
    setSortByOrder(orderToSort);
  };

  const unfoldAllRows = (tDataObj) => {
    const rowIdsToToggle = [];
    tDataObj.forEach((tRowData, index) => {
      if (tRowData.casNames.length > 1) {
        rowIdsToToggle.push(index);
      }
    });
    setUnfoldedRowsID(rowIdsToToggle);
  };

  const replaceColumnsTextByRole = (tBodyData) => {
    if (tBodyData.userRole === 'Super User') {
      return { ...tBodyData, casNames: [''], clientNames: [''] };
    } else if (tBodyData.userRole === 'Association User' && loggedAs !== ADMINISTRATOR_USER) {
      return { ...tBodyData, clientNames: 'N/A' };
    }
    return tBodyData;
  };

  const cellValueRenderFilter = (columnKey, value, rowData) => {
    return rowData.userRole === 'Super User' && columnKey === 'casNames'
      ? []
      : rowData.userRole === 'Super User' && columnKey === 'clientNames'
      ? []
      : rowData.userRole === 'Association User' && loggedAs !== ADMINISTRATOR_USER && columnKey === 'clientNames'
      ? 'N/A'
      : value;
  };

  const renderRowCells = (rowData, rowIndex) => {
    const showExpendIcon = isRowExpandable(rowData);
    const isExpanded = isRowExpanded(rowIndex);

    const rowDisplayData = replaceColumnsTextByRole({
      ...headerColumn.reduce((r, columnDef) => ({ ...r, [columnDef.name]: rowData[columnDef.name] }), {}),
      toggleIcon: showExpendIcon ? (
        <button
          className="ref-blue"
          aria-label={`click to ${isExpanded ? 'collapse' : 'expand'} user ${rowData.familyName}`}
          onKeyDown={(e) => (e.key === 'Enter' ? onClickUnfoldRow(e, rowIndex) : void 0)}
          onClick={(e) => onClickUnfoldRow(e, rowIndex)}>
          <i className={`far fa-${isExpanded ? 'minus' : 'plus'}-square`}></i>
        </button>
      ) : null,
    });

    const cellsData = Object.entries(rowDisplayData);

    const rowCells = cellsData.map(([colKey, col], index) => {
      const hasMultipleItems = Array.isArray(col) && col.length > 1;
      const value = hasMultipleItems ? col[0] : col;

      let tbClass = hasMultipleItems && isExpanded ? ['tb-unfolded'] : ['tb-folded'];
      if (index === 0) {
        tbClass.push('tb-first');
      } else if (index === cellsData.length - 1) {
        tbClass.push('tb-last');
      }

      return (
        <Td key={index} className={tbClass.join(' ')} colSpan={1} rowSpan={1}>
          {/* {} */}
          {hasMultipleItems ? (
            <ul
              className={[
                'list-no-style', 'list-aligned-items-height', `list-items-of-${colKey}`
              ].filter(identity).join(' ')}
              data-count={col.length}
            >
              {col.map((v, index) =>
                !isExpanded && index > 0 ? void 0 : <li key={`row_${rowIndex}_of_${colKey}_cell_item_extra_${index}-${v.replace(/\s/g, '_')}`}>{v}</li>
              )}
            </ul>
          ) : (
            highlighter(cellValueRenderFilter(colKey, value, rowData))
          )}
          {hasMultipleItems && !isExpanded ? renderMoreLabel(col.length, rowIndex, colKey, rowData) : void 0}
        </Td>
      );
    });

    return rowCells;
  };

  const highlighter = (text) => {
    if (!searchWords || typeof text !== 'string') return text;

    const index = !text ? -1 : text.toLowerCase().indexOf(searchWords.toLowerCase());
    if (index >= 0) {
      let notMatchStart = text.substring(0, index);
      let matchingString = text.substring(index, index + searchWords.length);
      let notMatchEnd = text.substring(index + searchWords.length);
      return (
        <div>
          {notMatchStart}
          <span className="highlights-opacity">{matchingString}</span>
          {notMatchEnd}
        </div>
      );
    } else {
      return <div>{text}</div>;
    }
  };

  const renderHeader = () => {
    const theadCells = headerColumn.map((thData, index) => {
      const { name, label } = thData;
      const thClass = [];
      if (index === 0) {
        thClass.push('th-first');
      } else if (index === headerColumn.length - 1) {
        thClass.push('th-last');
      }

      return (
        <Th className={thClass.join(' ')}
          key={index}
          aria-sort={name === sortByName ? sortByOrder : 'none'}>
          {index === 0 ? (
            <button
              id="button-toggle-expand-user-table-rows"
              tabIndex={0}
              aria-label={`users details all ${unfoldToggleActive ? 'expanded' : 'collapsed'}.`}
              className="ref-blue"
              onClick={() => setUnfoldToggle(!unfoldToggleActive)}
              onKeyDown={(e) => (e.key === 'Enter' ? setUnfoldToggle(!unfoldToggleActive) : void 0)}>
              <i className={`far fa-${unfoldToggleActive ? 'minus' : 'plus'}-square`}></i>
            </button>
          ) : (
            <span
              tabIndex={0}
              role="button"
              aria-label={`sort by ${label} ${sortByOrder || ''}.`}
              onClick={() => sort(name)}
              onKeyDown={(e) => (e.key === 'Enter' ? sort(name) : void 0)}>
              {label}
              {name === sortByName && sortByOrder ? (
                <i className={`absolute pLeft-5 pTop-2 fas ${sortChevronClassNameMap[sortByOrder]}`} />
              ) : (
                void 0
              )}
            </span>
          )}
        </Th>
      );
    });

    return (
      <Thead>
        <Tr role="row">{theadCells}</Tr>
      </Thead>
    );
  };

  const isRowExpanded = (rowId) => unfoldedRowsID.includes(rowId);

  const renderBody = () => {
    if (!bodyColumn) {
      return null;
    }

    if (sortByName) {
      bodyColumn.sort(dynamicSort(sortByName));
    }

    const tBodyRows = bodyColumn.map((tBodyData, rowIndex) => {
      return generateTableBodyRow(tBodyData, rowIndex, tBodyData);
    });

    return <Tbody className="manage-users-list-tbody">{tBodyRows}</Tbody>;
  };

  const renderMoreLabel = (casNamesLength, rowIndex, cellKey, rowDataObj) =>
    casNamesLength ? (
      <button
        tabIndex={0}
        className="ref-blue ref-text"
        aria-label={`click to show ${casNamesLength - 1} more ${cellKey} values of ${rowDataObj.familyName} user`}
        key={`row_${rowIndex}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onClickUnfoldRow(e, rowIndex);
          }
        }}
        onClick={(e) => {
          onClickUnfoldRow(e, rowIndex);
        }}>
        {`+ ${casNamesLength - 1} more`}
      </button>
    ) : (
      void 0
  );

  const isRowExpandable = (rowData) => {
    const casNamesCount = rowData.casNames.length;
    const clientNamesCount = Array.isArray(rowData.clientNames) ? rowData.clientNames.length : 0;
    const showExpendIcon = casNamesCount + clientNamesCount > 2;
    return showExpendIcon;
  };

  const generateTableBodyRow = (rowData, rowIndex) => {
    const showExpendIcon = isRowExpandable(rowData);
    const isExpanded = isRowExpanded(rowIndex);
    const ariaRowText = [
      rowData.givenName,
      rowData.familyName,
    ].join(' ');

    return (
      <Tr
        className={['table-row', showExpendIcon ? 'expandable' : null, isExpanded ? 'expanded' : null]
          .filter((v) => !!v)
          .join(' ')}
        onClick={() => onUserClick(rowData)}
        key={rowIndex}
        tabIndex={0}
        role="row"
        aria-label={`user table row for ${ariaRowText}.`}
        onKeyDown={(e) => (e.key === 'Enter' ? onUserClick(rowData) : void 0)}>
        {renderRowCells(rowData, rowIndex)}
      </Tr>
    );
  };

  const getTableRowsAndAlignInnerLists = () => {
    [...document.querySelectorAll('tbody.manage-users-list-tbody > tr')]
    .forEach(tBodyRow => {
      const listCas = tBodyRow.getElementsByClassName('list-items-of-casNames')[0];
      const listOrg = tBodyRow.getElementsByClassName('list-items-of-clientNames')[0];
      alignRowExpandableLists(listCas, listOrg);
    });
  };
  
  const alignRowExpandableLists = (listCas, listOrg) => {
    const count = listOrg ? parseInt(listOrg.getAttribute('data-count')) : 0;
    if (count) {
      const listItemsCas = listCas.querySelectorAll('li');
      const listItemsOrg = listOrg.querySelectorAll('li');
      [...listItemsCas]
        .forEach((li, index) => {
          listItemsOrg[index].style.height = 'auto';
          listItemsCas[index].style.height = 'auto';
          const casLiHeight = li.offsetHeight;
          const clientLiHeight = listItemsOrg[index].offsetHeight;
          const maxH = Math.max(casLiHeight, clientLiHeight);
          li.style.height = `${maxH}px`;
          listItemsOrg[index].style.height = `${maxH}px`;
        });
    }
  };

  useEffect(() => {
    setHeaderColumn(canViewColumnByRole(headerObj));
  }, []);

  useEffect(() => {
    const tDataCopy = tData;
    if (loggedAs === ADMINISTRATOR_USER && tDataCopy) {
      tDataCopy.forEach((data) => delete data.clientNames);
    }
    unfoldToggleActive ? unfoldAllRows(tDataCopy) : setUnfoldedRowsID([]);
    setBodyColumn(tDataCopy);
  }, [tData]);

  useEffect(() => {
    unfoldToggleActive ? unfoldAllRows(bodyColumn) : setUnfoldedRowsID([]);
  }, [unfoldToggleActive]);

  // track expanded state to align list items by height
  useEffect(() => {
    setTimeout(() => { getTableRowsAndAlignInnerLists(); }, 700);
  }, [unfoldedRowsID, unfoldToggleActive, dimensions, sideNavToggle]);

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth
      });   
    }
    window.addEventListener('resize', handleResize);
    return _ => {
      window.removeEventListener('resize', handleResize);
    };
  });

  return (
    <Table
      role={'grid'}
      id={id}
      tabIndex={0}
      aria-label={'Users List'}
      aria-rowcount={tData.length}
      aria-colcount={bodyColumn.length}
      key={'users-table'}
      className={'users-table'}>
      {renderHeader()}
      {renderBody()}
    </Table>
  );
}

export default UsersTable;
